import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MapPin, Clock, Coins, Plus, Send, X } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { prettyCity } from "@/components/CompanionCard";
import { Reveal } from "@/components/Reveal";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const flatTrigger = "rounded-none border-white/15 bg-transparent text-white focus:ring-velvet-gold";
const flatContent = "rounded-none border-white/15 bg-velvet-coal text-white max-h-72";
const inputCls = "w-full border-b border-white/15 bg-transparent py-2.5 text-sm text-white placeholder:text-white/30 focus:border-velvet-gold focus:outline-none";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function Requests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [cities, setCities] = useState([]);
  const [cats, setCats] = useState([]);
  const [genders, setGenders] = useState([]);
  const [form, setForm] = useState({
    city: "", town: "", category: "Companions", gender: "",
    date: "", time: "", budget: "", description: "",
  });

  const isAdvertiser = user && (user.role === "admin" || user.account_type === "advertiser");

  useEffect(() => { if (user === false) navigate("/login"); }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/requests").then((r) => setRequests(r.data)).catch(() => {});
    api.get("/locations").then((r) => setCities(r.data.flatMap((p) => p.cities))).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data.map((c) => c.name))).catch(() => {});
    api.get("/genders").then((r) => setGenders(r.data)).catch(() => {});
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.description.length < 10) {
      toast.error("Please describe what you're looking for (at least 10 characters)");
      return;
    }
    try {
      const { data } = await api.post("/requests", form);
      toast.success("Request posted — advertisers can now reply.");
      setShowForm(false);
      setForm({ city: "", town: "", category: "Companions", gender: "", date: "", time: "", budget: "", description: "" });
      navigate(`/requests/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const towns = cities.find((c) => c.slug === form.city)?.towns || [];

  if (!user) return null;

  return (
    <div className="pt-28 md:pt-36" data-testid="requests-page">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <Reveal>
          <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">Requests board</p>
          <h1 className="font-heading text-5xl font-semibold leading-none tracking-tight text-white md:text-7xl">
            Tell us what <span className="italic text-velvet-rose">you're looking for</span>
          </h1>
          <p className="mt-6 max-w-2xl text-sm font-light text-white/50">
            {isAdvertiser
              ? "Browse open requests from members. Reply with a quote to introduce yourself and one of your listings."
              : "Post a request. Verified advertisers can reply directly with a quote and a listing."}
          </p>
        </Reveal>

        {!isAdvertiser && !showForm && (
          <button onClick={() => setShowForm(true)} data-testid="requests-new-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-xs uppercase tracking-luxe text-black hover:bg-velvet-rose hover:text-white">
            <Plus size={14} /> Post a request
          </button>
        )}

        {showForm && (
          <motion.form initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            onSubmit={submit} className="mt-10 grid grid-cols-1 gap-5 border border-white/10 p-6 md:grid-cols-2" data-testid="requests-form">
            <div>
              <label className="mb-2 block text-[10px] uppercase tracking-luxe text-white/40">City</label>
              <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v, town: "" })}>
                <SelectTrigger className={flatTrigger} data-testid="req-city"><SelectValue placeholder="Any city" /></SelectTrigger>
                <SelectContent className={flatContent}>{cities.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-2 block text-[10px] uppercase tracking-luxe text-white/40">Town / Area</label>
              <Select value={form.town || "none"} onValueChange={(v) => setForm({ ...form, town: v === "none" ? "" : v })}>
                <SelectTrigger className={flatTrigger}><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent className={flatContent}><SelectItem value="none">— Any —</SelectItem>{towns.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-2 block text-[10px] uppercase tracking-luxe text-white/40">Category</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className={flatTrigger}><SelectValue /></SelectTrigger>
                <SelectContent className={flatContent}>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-2 block text-[10px] uppercase tracking-luxe text-white/40">Gender</label>
              <Select value={form.gender || "any"} onValueChange={(v) => setForm({ ...form, gender: v === "any" ? "" : v })}>
                <SelectTrigger className={flatTrigger}><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent className={flatContent}><SelectItem value="any">Any</SelectItem>{genders.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <input type="date" placeholder="Date" value={form.date} data-testid="req-date"
              onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
            <input type="time" placeholder="Time" value={form.time} data-testid="req-time"
              onChange={(e) => setForm({ ...form, time: e.target.value })} className={inputCls} />
            <input placeholder="Budget (e.g. R2500)" value={form.budget} data-testid="req-budget"
              onChange={(e) => setForm({ ...form, budget: e.target.value })} className={`${inputCls} md:col-span-2`} />
            <textarea placeholder="Describe what you're looking for — vibe, evening plan, preferences, anything to help advertisers reply well." value={form.description}
              data-testid="req-description"
              rows={4} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} md:col-span-2`} />
            <div className="flex gap-3 md:col-span-2">
              <button type="button" onClick={() => setShowForm(false)}
                className="rounded-full border border-white/15 px-5 py-2.5 text-xs uppercase tracking-luxe text-white/60 hover:border-white/40">Cancel</button>
              <button type="submit" data-testid="req-submit"
                className="flex-1 rounded-full bg-white py-3 text-xs uppercase tracking-luxe text-black hover:bg-velvet-rose hover:text-white">
                Post request
              </button>
            </div>
          </motion.form>
        )}

        <div className="mt-16 space-y-4" data-testid="requests-list">
          <p className="text-xs uppercase tracking-luxe text-white/40">
            {requests.length} {isAdvertiser ? "open request" : "of your request"}{requests.length !== 1 ? "s" : ""}
          </p>
          {requests.length === 0 ? (
            <div className="border border-white/10 p-16 text-center">
              <p className="font-heading text-2xl font-light text-white/50">
                {isAdvertiser ? "No open requests right now — check back soon." : "You haven't posted a request yet."}
              </p>
            </div>
          ) : requests.map((r) => (
            <Link key={r.id} to={`/requests/${r.id}`} data-testid={`req-card-${r.id}`}
              className="block border border-white/10 p-5 transition-colors hover:border-velvet-gold">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-3 text-xs uppercase tracking-luxe text-velvet-gold">
                    {r.category}
                    {r.gender && <span className="text-white/40">· {r.gender}</span>}
                    {r.city && <span className="flex items-center gap-1 text-white/40"><MapPin size={11} /> {prettyCity(r.city)}{r.town ? `, ${r.town}` : ""}</span>}
                  </p>
                  <p className="mt-2 text-sm font-light text-white/70 line-clamp-2">{r.description}</p>
                  <div className="mt-3 flex flex-wrap gap-4 text-[10px] uppercase tracking-luxe text-white/40">
                    {r.date && <span className="flex items-center gap-1"><Clock size={10} /> {r.date} {r.time}</span>}
                    {r.budget && <span className="flex items-center gap-1"><Coins size={10} /> {r.budget}</span>}
                    <span>{timeAgo(r.created_at)}</span>
                    <span className="text-velvet-rose">{r.reply_count || 0} repl{(r.reply_count || 0) === 1 ? "y" : "ies"}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function RequestDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [myListings, setMyListings] = useState([]);
  const [reply, setReply] = useState({ listing_slug: "", message: "", quote: "" });
  const isAdvertiser = user && (user.role === "admin" || user.account_type === "advertiser");

  useEffect(() => { if (user === false) navigate("/login"); }, [user, navigate]);

  const load = () => api.get(`/requests/${id}`).then((r) => setData(r.data)).catch(() => setData({ notfound: true }));
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [id, user]);
  useEffect(() => {
    if (isAdvertiser) api.get("/me/listings").then((r) => setMyListings(r.data)).catch(() => {});
  }, [isAdvertiser]);

  const sendReply = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/requests/${id}/reply`, reply);
      toast.success("Reply sent");
      setReply({ listing_slug: "", message: "", quote: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const closeRequest = async () => {
    if (!window.confirm("Close this request?")) return;
    await api.delete(`/requests/${id}`);
    toast.success("Request closed");
    navigate("/requests");
  };

  if (!user || !data) return null;
  if (data.notfound) {
    return <div className="pt-40 text-center"><p className="font-heading text-3xl text-white/60">Request not found</p><Link to="/requests" className="mt-6 inline-block text-xs uppercase tracking-luxe text-velvet-gold">Back to requests</Link></div>;
  }

  const { request: req, replies } = data;
  const isOwner = req.member_id === user.id;

  return (
    <div className="pt-28 md:pt-36" data-testid="request-detail-page">
      <div className="mx-auto max-w-3xl px-6 md:px-12">
        <Link to="/requests" className="text-[10px] uppercase tracking-luxe text-white/50 hover:text-velvet-gold">← All requests</Link>
        <div className="mt-8 border border-white/10 p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-luxe text-velvet-gold">
                {req.category}{req.gender && ` · ${req.gender}`}
              </p>
              <h1 className="mt-2 font-heading text-3xl font-light text-white md:text-4xl">
                {req.city ? prettyCity(req.city) : "Anywhere"}{req.town ? `, ${req.town}` : ""}
              </h1>
              <p className="mt-4 whitespace-pre-wrap text-sm font-light text-white/70">{req.description}</p>
              <div className="mt-4 flex flex-wrap gap-4 text-[10px] uppercase tracking-luxe text-white/40">
                {req.date && <span>{req.date} {req.time}</span>}
                {req.budget && <span>Budget: {req.budget}</span>}
                <span>Status: <b className={req.status === "open" ? "text-[#25D366]" : "text-white/50"}>{req.status}</b></span>
              </div>
            </div>
            {isOwner && req.status === "open" && (
              <button onClick={closeRequest} data-testid="req-close" className="rounded-full border border-velvet-rose px-4 py-2 text-[10px] uppercase tracking-luxe text-velvet-rose hover:bg-velvet-rose hover:text-white">
                <X size={11} className="inline" /> Close
              </button>
            )}
          </div>
        </div>

        <div className="mt-10">
          <p className="text-xs uppercase tracking-luxe text-velvet-gold">
            {replies.length} repl{replies.length === 1 ? "y" : "ies"}
          </p>
          <div className="mt-4 space-y-4" data-testid="request-replies">
            {replies.map((r) => (
              <div key={r.id} className="border border-white/10 p-5">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-white">{r.advertiser_name}</p>
                  <span className="text-[10px] uppercase tracking-luxe text-white/40">{timeAgo(r.created_at)}</span>
                </div>
                {r.listing_name && (
                  <Link to={`/companion/${r.listing_slug}`} className="mt-1 inline-block text-[10px] uppercase tracking-luxe text-velvet-gold hover:text-velvet-rose">
                    Listing: {r.listing_name} →
                  </Link>
                )}
                <p className="mt-3 whitespace-pre-wrap text-sm font-light text-white/70">{r.message}</p>
                {r.quote && (
                  <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-velvet-gold/40 bg-velvet-gold/5 px-3 py-1 text-xs text-velvet-gold">
                    <Coins size={11} /> Quote: {r.quote}
                  </p>
                )}
              </div>
            ))}
            {replies.length === 0 && (
              <p className="border border-white/10 p-8 text-center text-sm font-light text-white/40">
                No replies yet.
              </p>
            )}
          </div>
        </div>

        {isAdvertiser && req.status === "open" && (
          <form onSubmit={sendReply} className="mt-10 space-y-4 border border-velvet-gold/40 bg-velvet-gold/5 p-6" data-testid="req-reply-form">
            <p className="text-xs uppercase tracking-luxe text-velvet-gold">Reply as advertiser</p>
            {myListings.length > 0 && (
              <Select value={reply.listing_slug || "none"} onValueChange={(v) => setReply({ ...reply, listing_slug: v === "none" ? "" : v })}>
                <SelectTrigger className={flatTrigger} data-testid="req-reply-listing"><SelectValue placeholder="Attach a listing (optional)" /></SelectTrigger>
                <SelectContent className={flatContent}>
                  <SelectItem value="none">— None —</SelectItem>
                  {myListings.map((l) => <SelectItem key={l.slug} value={l.slug}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <input placeholder="Your quote (e.g. R3000)" value={reply.quote} data-testid="req-reply-quote"
              onChange={(e) => setReply({ ...reply, quote: e.target.value })} className={inputCls} />
            <textarea placeholder="Your message" required rows={4} value={reply.message} data-testid="req-reply-message"
              onChange={(e) => setReply({ ...reply, message: e.target.value })} className={inputCls} />
            <button type="submit" data-testid="req-reply-submit"
              className="flex items-center gap-2 rounded-full bg-white px-6 py-3 text-xs uppercase tracking-luxe text-black hover:bg-velvet-rose hover:text-white">
              <Send size={13} /> Send reply
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
